import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BlobServiceClient } from '@azure/storage-blob';
import { environment } from '../../environments/environment';

export interface OnboardPayload {
  businessCreateRequest: {
    name: string;
    logo: string;
    favIcon: string;
    bannerImage: string;
    entityName: string;
    primaryColor: string;
    adminName: string;
    adminMobile: string;
    adminEmail: string;
    allowsMaturityExtension: boolean;
    domain: string;
    erpDomainName: string;
    minSeqDigits: number;
  };
  store: {
    id: string;
    storeName: string;
    storeImageUrl: string;
    storeRepresentativeId: string;
    storeWebUrl: string;
    storeQrUrl: string;
    addressDetails: {
      country: string;
      state: string;
      city: string;
      locality: string;
      addressLine: string;
      addressDescription: string;
      pinCode: number;
      geoLocation: { latitude: number; longitude: number };
    };
    storeContactDetails: {
      countryCode: string;
      mobile: string;
      email: string;
      contactPerson: string;
    };
    contactInfo: {
      WHATSAPP: string;
      CALL: string;
    };
  };
}

export interface SelfOnboardAddress {
  country: string;
  state: string;
  city: string;
  locality: string;
  addressLine: string;
  addressDescription: string;
  pinCode: number;
  geoLocation: { latitude: number; longitude: number };
}

export interface SelfOnboardPayload {
  store: {
    storeName: string;
    addressDetails: SelfOnboardAddress;
    storeContactDetails: {
      countryCode: string;
      mobile: string;
      email: string;
      contactPerson: string;
    };
  };
  businessName: string;
  businessEntityName: string;
  addressDetails: SelfOnboardAddress;
  mobile: string;
  name: string;
  email: string;
}

export interface SelfOnboardResponse {
  data?: {
    staff?: {
      staffId?: string | null;
      token?: string | null;
    } | null;
    erpDomainName?: string | null;
    business?: {
      erpDomainName?: string | null;
    } | null;
  } | null;
  erpDomainName?: string | null;
}

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  constructor(private http: HttpClient) {}

  // Main onboarding API call
  onboard(payload: OnboardPayload): Observable<any> {
    return this.http.post(`${environment.baseUrl}business/ssp/self/onboard`, payload);
  }

  // Simplified self onboarding used by the business setup form
  selfOnboard(payload: SelfOnboardPayload): Observable<SelfOnboardResponse> {
    return this.http.post<SelfOnboardResponse>(
      `${environment.baseUrl}business/ssp/self/onboard`,
      payload,
    );
  }

  // Direct upload to Azure Blob Storage – returns an object with url property
  async uploadFile(file: File, folderName: string): Promise<{ url: string }> {
    const sasToken = environment.sasToken;
    const accountName = environment.accountName;
    const containerName = environment.containerName;

    const blobServiceClient = new BlobServiceClient(
      `https://${accountName}.blob.core.windows.net${sasToken}`
    );
    const containerClient = blobServiceClient.getContainerClient(containerName);
    // Optional: generate unique blob name to avoid collisions
    const timestamp = Date.now();
    const uniqueName = `${timestamp}_${file.name}`;
    const blobName = uniqueName;
    const blockBlobClient = containerClient.getBlockBlobClient(`${folderName}/${blobName}`);

    await blockBlobClient.uploadData(file, {
      blobHTTPHeaders: { blobContentType: file.type },
    });

    // Return object with url property (matches component expectation)
    return { url: blockBlobClient.url };
  }
}